import React from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useClient } from '@/context/ClientContext';
import { APP } from '@/constants/testIds';
import {
  LayoutDashboard, Gauge, Swords, Sparkles, GitBranch, Store,
  FileText, Bot, Settings, Sun, Moon, LogOut, ChevronRight, Plus, UserCog, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// roles: which roles can see each item
const NAV = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: APP.navDashboard, roles: ['admin', 'member', 'client'] },
  { to: '/clients', label: 'All Clients', icon: Users, testid: 'nav-clients', roles: ['admin', 'member'] },
  { to: '/assessments', label: 'Assessments', icon: Gauge, testid: APP.navAssessments, roles: ['admin', 'member', 'client'] },
  { to: '/competitors', label: 'Competitors', icon: Swords, testid: APP.navCompetitors, roles: ['admin', 'member'] },
  { to: '/content', label: 'Content Factory', icon: Sparkles, testid: APP.navContent, roles: ['admin', 'member'] },
  { to: '/simulator', label: 'Simulator', icon: ChevronRight, testid: APP.navSimulator, roles: ['admin', 'member', 'client'] },
  { to: '/graph', label: 'Authority Graph', icon: GitBranch, testid: APP.navGraph, roles: ['admin', 'member', 'client'] },
  { to: '/marketplace', label: 'Marketplace', icon: Store, testid: APP.navMarketplace, roles: ['admin', 'member'] },
  { to: '/reports', label: 'Reports', icon: FileText, testid: APP.navReports, roles: ['admin', 'member', 'client'] },
  { to: '/agents', label: 'Agentic Swarm', icon: Bot, testid: APP.navAgents, roles: ['admin', 'member'] },
  { to: '/team', label: 'Team', icon: UserCog, testid: 'nav-team', roles: ['admin'] },
  { to: '/settings', label: 'Settings', icon: Settings, testid: APP.navSettings, roles: ['admin', 'member'] },
];

const ROLE_BADGE = {
  admin: { label: 'Admin', className: 'bg-primary/10 text-primary border-primary/30' },
  member: { label: 'Member', className: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30' },
  client: { label: 'Client', className: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30' },
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { clients, activeId, selectClient } = useClient();
  const navigate = useNavigate();

  const role = user?.role || 'member';
  const visibleNav = NAV.filter(n => n.roles.includes(role));
  const canCreateClient = role === 'admin' || role === 'member';
  const showClientPicker = (role === 'admin' || role === 'member') && clients.length > 0;
  const roleBadge = ROLE_BADGE[role] || ROLE_BADGE.member;

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-border bg-card/40">
        <Link to="/dashboard" data-testid={APP.logoLink} className="px-6 py-5 border-b border-border flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-display text-sm">A</span>
          </div>
          <div className="font-display text-lg tracking-tight">AuthorityOS™</div>
        </Link>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {visibleNav.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`
              }
            >
              <Icon className="h-4 w-4" strokeWidth={1.5} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-border space-y-2">
          {user && (
            <div className="flex items-center gap-2 px-2">
              {user.picture ? (
                <div
                  role="img"
                  aria-label={user.name || 'avatar'}
                  className="h-8 w-8 rounded-full border border-border bg-cover bg-center"
                  style={{ backgroundImage: `url(${user.picture})` }}
                />
              ) : (
                <div className="h-8 w-8 rounded-full bg-muted" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {user.first_name || user.name?.split(' ')[0] || user.email}
                </div>
                <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                <Badge variant="outline" className={`mt-1 text-[10px] uppercase tracking-wider ${roleBadge.className}`}>{roleBadge.label}</Badge>
              </div>
            </div>
          )}
          <Button
            data-testid={APP.signOut}
            variant="ghost"
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="glass sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 py-3">
            <div className="flex items-center gap-3">
              <div className="lg:hidden font-display text-lg">AuthorityOS™</div>
              {showClientPicker && (
                <Select value={activeId || ''} onValueChange={selectClient}>
                  <SelectTrigger data-testid={APP.clientPicker} className="w-[260px] h-9">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(c => (
                      <SelectItem key={c.client_id} value={c.client_id}>
                        {c.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {role === 'client' && clients[0] && (
                <div className="px-3 py-1.5 rounded-md border border-border bg-card text-sm font-medium">
                  {clients[0].company_name}
                </div>
              )}
              {canCreateClient && (
                <Button size="sm" variant="outline" onClick={() => navigate('/clients')}>
                  <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> New client
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid={APP.themeToggle}
                variant="ghost"
                size="icon"
                onClick={toggle}
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun className="h-4 w-4" strokeWidth={1.5} /> : <Moon className="h-4 w-4" strokeWidth={1.5} />}
              </Button>
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 lg:p-10 max-w-[1400px] w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}
